import type { CSSProperties } from 'react';
import type { FaqVisualType } from '../../data/faqData';
import './FaqVisual.css';

interface FaqVisualProps {
  type: FaqVisualType;
}

export function FaqVisual({ type }: FaqVisualProps) {
  if (type === 'none') return null;
  return (
    <div className={`saas-faqviz saas-faqviz--${type}`} aria-hidden="true">
      {type === 'hero' && <HeroGraphic />}
      {type === 'projects' && <ProjectsGraphic />}
      {type === 'workspace' && <WorkspaceGraphic />}
      {type === 'ai' && <AiGraphic />}
      {type === 'chat' && <ChatGraphic />}
      {type === 'gallery' && <GalleryGraphic />}
      {type === 'media' && <MediaGraphic />}
      {type === 'search' && <SearchGraphic />}
      {type === 'profile' && <ProfileGraphic />}
      {type === 'settings' && <SettingsGraphic />}
      {type === 'security' && <SecurityGraphic />}
      {type === 'calendar' && <CalendarGraphic />}
      {type === 'community' && <CommunityGraphic />}
      {type === 'workflow' && <WorkflowGraphic />}
      {type === 'platform' && <PlatformGraphic />}
      {type === 'editor' && <EditorGraphic />}
      {type === 'render' && <RenderGraphic />}
      {type === 'calls' && <CallsGraphic />}
    </div>
  );
}

function HeroGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <circle className="saas-faqviz__orb" cx="100" cy="60" r="40" fill="var(--faq-accent)" opacity="0.2" />
      <circle className="saas-faqviz__orb" cx="100" cy="60" r="24" fill="var(--faq-cyan)" opacity="0.35" style={{ animationDelay: '0.3s' } as CSSProperties} />
      <path className="saas-faqviz__draw" d="M70 60 L95 75 L130 45" stroke="var(--faq-accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProjectsGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      {[0, 1, 2].map((i) => (
        <rect key={i} className="saas-faqviz__card" x={28 + i * 52} y={30 + i * 6} width="44" height="56" rx="6" fill="var(--faq-surface)" stroke="var(--faq-border)" strokeWidth="1.2" style={{ animationDelay: `${i * 0.12}s` } as CSSProperties} />
      ))}
      <rect className="saas-faqviz__lane" x="36" y="42" width="28" height="6" rx="2" fill="var(--faq-accent)" opacity="0.5" />
    </svg>
  );
}

function WorkspaceGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect x="16" y="22" width="168" height="76" rx="6" stroke="var(--faq-border)" fill="var(--faq-surface-2)" />
      <rect className="saas-faqviz__lane" x="24" y="32" width="152" height="12" rx="3" fill="var(--faq-accent)" opacity="0.35" />
      <rect className="saas-faqviz__lane" x="24" y="50" width="100" height="12" rx="3" fill="var(--faq-cyan)" opacity="0.4" style={{ animationDelay: '0.15s' } as CSSProperties} />
      <rect className="saas-faqviz__lane" x="24" y="68" width="120" height="12" rx="3" fill="var(--faq-pink)" opacity="0.35" style={{ animationDelay: '0.3s' } as CSSProperties} />
      <line className="saas-faqviz__playhead" x1="110" y1="22" x2="110" y2="98" stroke="var(--faq-cyan)" strokeWidth="1.5" strokeDasharray="3 2" />
    </svg>
  );
}

function AiGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <circle className="saas-faqviz__ai-orb" cx="100" cy="55" r="32" fill="var(--faq-accent)" opacity="0.15" />
      <circle className="saas-faqviz__ai-orb" cx="85" cy="48" r="8" fill="var(--faq-cyan)" opacity="0.6" style={{ animationDelay: '0.2s' } as CSSProperties} />
      <circle className="saas-faqviz__ai-orb" cx="115" cy="62" r="6" fill="var(--faq-pink)" opacity="0.5" style={{ animationDelay: '0.4s' } as CSSProperties} />
      <path className="saas-faqviz__draw" d="M55 85 Q100 95 145 85" stroke="var(--faq-accent)" strokeWidth="2" fill="none" />
      <text x="92" y="58" fill="var(--faq-text)" fontSize="14" opacity="0.7">✨</text>
    </svg>
  );
}

function ChatGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect className="saas-faqviz__card" x="30" y="28" width="90" height="28" rx="10" fill="var(--faq-accent)" opacity="0.25" stroke="var(--faq-accent)" />
      <rect className="saas-faqviz__card" x="80" y="62" width="90" height="28" rx="10" fill="var(--faq-cyan)" opacity="0.2" stroke="var(--faq-cyan)" style={{ animationDelay: '0.2s' } as CSSProperties} />
      <circle cx="48" cy="42" r="4" fill="var(--faq-accent)" className="saas-faqviz__dot" />
      <circle cx="58" cy="42" r="4" fill="var(--faq-accent)" className="saas-faqviz__dot" style={{ animationDelay: '0.15s' } as CSSProperties} />
      <circle cx="68" cy="42" r="4" fill="var(--faq-accent)" className="saas-faqviz__dot" style={{ animationDelay: '0.3s' } as CSSProperties} />
    </svg>
  );
}

function GalleryGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect key={i} className="saas-faqviz__tile" x={24 + (i % 3) * 54} y={24 + Math.floor(i / 3) * 40} width="46" height="34" rx="4" fill="var(--faq-surface)" stroke="var(--faq-border)" style={{ animationDelay: `${i * 0.08}s` } as CSSProperties} />
      ))}
      <circle cx="42" cy="38" r="6" fill="var(--faq-cyan)" opacity="0.5" />
    </svg>
  );
}

function MediaGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect x="40" y="30" width="120" height="60" rx="6" fill="var(--faq-surface-2)" stroke="var(--faq-border)" />
      <polygon className="saas-faqviz__play-btn" points="92,48 92,72 112,60" fill="var(--faq-cyan)" />
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} className="saas-faqviz__bar" x={50 + i * 22} y={95 - (i % 3) * 8} width="8" height={12 + (i % 4) * 6} rx="2" fill="var(--faq-accent)" opacity="0.5" style={{ animationDelay: `${i * 0.1}s` } as CSSProperties} />
      ))}
    </svg>
  );
}

function SearchGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <circle cx="88" cy="52" r="28" stroke="var(--faq-accent)" strokeWidth="2.5" fill="var(--faq-surface)" opacity="0.5" />
      <line x1="108" y1="72" x2="130" y2="94" stroke="var(--faq-accent)" strokeWidth="3" strokeLinecap="round" />
      <rect className="saas-faqviz__lane" x="50" y="88" width="100" height="8" rx="4" fill="var(--faq-cyan)" opacity="0.3" />
    </svg>
  );
}

function ProfileGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <circle cx="100" cy="42" r="22" fill="var(--faq-accent)" opacity="0.3" stroke="var(--faq-accent)" />
      <path d="M60 95 Q100 70 140 95" fill="var(--faq-cyan)" opacity="0.25" />
      <rect className="saas-faqviz__card" x="130" y="28" width="40" height="40" rx="20" fill="var(--faq-pink)" opacity="0.2" stroke="var(--faq-pink)" />
    </svg>
  );
}

function SettingsGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <circle className="saas-faqviz__gear" cx="100" cy="60" r="28" stroke="var(--faq-accent)" strokeWidth="2" fill="var(--faq-surface)" />
      <circle cx="100" cy="60" r="10" fill="var(--faq-cyan)" opacity="0.4" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
        <rect key={i} x="96" y="28" width="8" height="14" rx="2" fill="var(--faq-accent)" opacity="0.5" transform={`rotate(${deg} 100 60)`} />
      ))}
    </svg>
  );
}

function SecurityGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <path d="M100 25 L150 45 V75 Q150 95 100 105 Q50 95 50 75 V45 Z" fill="var(--faq-accent)" opacity="0.2" stroke="var(--faq-accent)" strokeWidth="2" />
      <path className="saas-faqviz__draw" d="M82 62 L95 75 L118 50" stroke="var(--faq-cyan)" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function CalendarGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect x="50" y="30" width="100" height="70" rx="6" fill="var(--faq-surface)" stroke="var(--faq-border)" />
      <rect x="50" y="30" width="100" height="18" rx="6" fill="var(--faq-accent)" opacity="0.35" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect key={i} className="saas-faqviz__tile" x={58 + (i % 3) * 30} y={56 + Math.floor(i / 3) * 18} width="22" height="14" rx="2" fill="var(--faq-cyan)" opacity={i === 2 ? 0.7 : 0.2} style={{ animationDelay: `${i * 0.1}s` } as CSSProperties} />
      ))}
    </svg>
  );
}

function CommunityGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      {[[70, 50], [100, 38], [130, 50]].map(([cx, cy], i) => (
        <g key={i}>
          <circle className="saas-faqviz__dot" cx={cx} cy={cy} r="14" fill="var(--faq-accent)" opacity="0.25" style={{ animationDelay: `${i * 0.15}s` } as CSSProperties} />
          <circle cx={cx} cy={cy - 4} r="6" fill="var(--faq-cyan)" opacity="0.5" />
        </g>
      ))}
      <path d="M55 85 Q100 70 145 85" stroke="var(--faq-accent)" strokeWidth="1.5" fill="none" opacity="0.4" />
    </svg>
  );
}

function WorkflowGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      {[40, 90, 140].map((x, i) => (
        <g key={x}>
          <circle className="saas-faqviz__seq-node" cx={x} cy="60" r="14" fill="var(--faq-accent)" opacity="0.3" stroke="var(--faq-accent)" style={{ animationDelay: `${i * 0.2}s` } as CSSProperties} />
          {i < 2 && <path className="saas-faqviz__arrow" d={`M${x + 16} 60 L${x + 34} 60`} stroke="var(--faq-cyan)" strokeWidth="2" markerEnd="url(#arr)" />}
        </g>
      ))}
      <defs><marker id="arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6" fill="var(--faq-cyan)" /></marker></defs>
    </svg>
  );
}

function PlatformGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect x="30" y="40" width="50" height="40" rx="6" className="saas-faqviz__card" fill="var(--faq-accent)" opacity="0.2" stroke="var(--faq-accent)" />
      <rect x="75" y="30" width="50" height="40" rx="6" className="saas-faqviz__card" fill="var(--faq-cyan)" opacity="0.2" stroke="var(--faq-cyan)" style={{ animationDelay: '0.15s' } as CSSProperties} />
      <rect x="120" y="40" width="50" height="40" rx="6" className="saas-faqviz__card" fill="var(--faq-pink)" opacity="0.2" stroke="var(--faq-pink)" style={{ animationDelay: '0.3s' } as CSSProperties} />
    </svg>
  );
}

function EditorGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect x="35" y="28" width="130" height="64" rx="6" fill="var(--faq-surface-2)" stroke="var(--faq-border)" />
      <line x1="50" y1="48" x2="150" y2="48" stroke="var(--faq-muted)" strokeWidth="1" opacity="0.4" />
      <line x1="50" y1="62" x2="130" y2="62" stroke="var(--faq-muted)" strokeWidth="1" opacity="0.4" />
      <rect className="saas-faqviz__lane" x="50" y="76" width="60" height="8" rx="2" fill="var(--faq-accent)" opacity="0.5" />
      <path className="saas-faqviz__draw" d="M145 35 L155 45 L140 60" stroke="var(--faq-cyan)" strokeWidth="2" fill="none" />
    </svg>
  );
}

function RenderGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <rect x="50" y="35" width="100" height="50" rx="4" fill="var(--faq-surface)" stroke="var(--faq-border)" />
      <rect className="saas-faqviz__lufs-fill" x="60" y="78" width="80" height="8" rx="4" fill="var(--faq-accent)" opacity="0.5" />
      <text x="82" y="68" fill="var(--faq-cyan)" fontSize="11" opacity="0.8">MP4</text>
    </svg>
  );
}

function CallsGraphic() {
  return (
    <svg className="saas-faqviz__svg" viewBox="0 0 200 120" fill="none">
      <circle className="saas-faqviz__rec-dot" cx="100" cy="55" r="30" fill="var(--faq-accent)" opacity="0.15" stroke="var(--faq-accent)" strokeWidth="2" />
      <path d="M85 45 Q100 35 115 45 L115 70 Q100 80 85 70 Z" fill="var(--faq-cyan)" opacity="0.4" />
      <rect x="70" y="88" width="60" height="10" rx="5" fill="var(--faq-pink)" opacity="0.3" className="saas-faqviz__lane" />
    </svg>
  );
}
